import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MUIDataTable from 'mui-datatables';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Box, Stack, TablePagination, Typography, useMediaQuery } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import TableDetailDrawer from 'components/shared/tables/responsive/TableDetailDrawer';
import { columnLabel, isVisibleColumn, renderCell } from 'components/shared/tables/responsive/cellRenderer';

/**
 * Reemplazo drop-in de <MUIDataTable>: en md+ es la MISMA tabla de siempre; bajo md las
 * filas apiladas (responsive 'vertical') se convierten en CARDS que abren un panel lateral
 * con la información completa y las acciones dentro (patrón "Detalle de la sesión").
 *
 * Migrar una tabla = cambiar el import + (opcional) pasar `cardConfig`:
 *   { titleColumn, summaryColumns, actionsColumnName }
 * Sin config: título = primera columna visible, resumen = las 2 siguientes.
 * La selección múltiple (borrado masivo) se queda en desktop: en mobile la acción es por
 * fila dentro del panel.
 */
function ResponsiveTable({ columns, data, options = {}, className = undefined, cardConfig = null }) {
  const { t } = useTranslation(['tables']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [detail, setDetail] = useState(null); // { row, rowIndex }
  // El drawer se desmontaría al cerrar y MUI se saltaría la animación de salida: conservamos
  // la última fila abierta para seguir pintándola mientras el panel se desliza hacia fuera.
  const lastDetail = useRef(null);
  if (detail) lastDetail.current = detail;
  const shown = detail || lastDetail.current;

  if (!isMobile) {
    return <MUIDataTable className={className} columns={columns} data={data} options={options} />;
  }

  const visible = columns.filter(isVisibleColumn);
  const actionsName = cardConfig?.actionsColumnName || 'actions';
  const fieldColumns = visible.filter((c) => c.name !== actionsName);

  const titleColumn =
    (cardConfig?.titleColumn && columns.find((c) => c.name === cardConfig.titleColumn)) ||
    fieldColumns[0] ||
    null;
  const summaryColumns = cardConfig?.summaryColumns
    ? cardConfig.summaryColumns.map((name) => columns.find((c) => c.name === name)).filter(Boolean)
    : fieldColumns.slice(1, 3);

  const page = options?.page ?? 0;
  const rowsPerPage = options?.rowsPerPage ?? data.length ?? 10;
  const count = options?.count ?? data.length;

  return (
    <Box>
      <Stack spacing={1.25}>
        {data.map((row, rowIndex) => (
          <Stack
            key={row.id ?? rowIndex}
            direction="row"
            spacing={1.5}
            alignItems="center"
            role="button"
            tabIndex={0}
            onClick={() => setDetail({ row, rowIndex })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setDetail({ row, rowIndex });
              }
            }}
            sx={{
              p: 1.75,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.content',
              boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
              cursor: 'pointer',
              transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), transform 200ms cubic-bezier(0.32,0.72,0,1)',
              '&:hover': { backgroundColor: 'surface.hover' },
              '&:active': { transform: 'scale(0.98)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
            }}>
            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
              {titleColumn && (
                <Box sx={{ fontWeight: 700, '& > *': { maxWidth: '100%' } }}>
                  {renderCell(titleColumn, row, rowIndex, columns.indexOf(titleColumn), data)}
                </Box>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {summaryColumns.map((column) => (
                  <Stack key={column.name} spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.62rem' }}>
                      {columnLabel(column)}
                    </Typography>
                    <Box sx={{ fontSize: '0.8rem', '& > *': { maxWidth: '100%' } }}>
                      {renderCell(column, row, rowIndex, columns.indexOf(column), data)}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Stack>
            <ChevronRight sx={{ color: 'text.disabled', flex: 'none' }} />
          </Stack>
        ))}
      </Stack>

      {/* Misma paginación serverSide que la tabla (page 0-based, igual que MUIDataTable). */}
      {options?.onChangePage && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          onPageChange={(_, newPage) => options.onChangePage(newPage)}
          labelDisplayedRows={({ from, to, count: total }) =>
            `${from}-${to} ${t('of', { ns: 'common', defaultValue: 'de' })} ${total}`
          }
        />
      )}

      <TableDetailDrawer
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        columns={columns}
        row={shown?.row || null}
        rowIndex={shown?.rowIndex || 0}
        allRows={data}
        actionsColumnName={actionsName}
      />
    </Box>
  );
}

ResponsiveTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  options: PropTypes.object,
  className: PropTypes.string,
  /** { titleColumn, summaryColumns: [names], actionsColumnName } */
  cardConfig: PropTypes.object
};

export default ResponsiveTable;
